import React, { useMemo } from 'react';
import { ForgeListItem } from '@tylertech/forge-react';
import { useSortable } from '@dnd-kit/sortable';
import cx from 'classnames';
import { uniqueId } from 'lodash';

import { I18n } from 'common/i18n';
import { DragHandleIcon } from './DragHandleIcon';
import { DragDropElementWrapper } from './index';
import { getSortableStyle } from './sortableStyles';

const scope = 'common';

interface SortableForgeListItemProps<T = unknown> {
  item: DragDropElementWrapper<T>;
  isDragOverlay?: boolean;
  iconName?: string;
}

export const SortableForgeListItem = <T = unknown,>({
  item,
  isDragOverlay = false,
  iconName = 'drag_handle',
}: SortableForgeListItemProps<T>) => {
  // Generate a stable default ID if none provided
  const sortableId = useMemo(() => item.id || uniqueId('sortable-forge-list-'), [item.id]);
  const ariaLabel = I18n.t('drag_to_reorder_aria_label', { scope });
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sortableId,
    data: {
      type: 'sortable-item',
      item: item,
    },
    disabled: !item.dragAndDroppable,
  });

  const style = getSortableStyle({
    transform,
    transition,
    isDragging,
    isDragOverlay,
  });

  // Regular sortable item with wrapper -
  // ForgeListItem use innerRef instead of the standard React ref prop
  // because they're wrapped web components via the reactify-wc library. So instead we use a wrapper div
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cx('sortable-item-wrapper', { 'is-dragging': isDragging })}
    >
      <ForgeListItem
        {...item.additionalItemProps}
        className={cx('sortable-item', item.className, {
          dragging: isDragging,
        })}
      >
        <button>
          <div className="sortable-item-content">
            <div {...attributes} {...listeners} className="drag-handle" aria-label={ariaLabel}>
              <DragHandleIcon name={iconName} isDraggable={item.dragAndDroppable} />
            </div>
            <div className="main-content">{item.element}</div>
            {item.trailingElement && <div className="trailing-content">{item.trailingElement}</div>}
          </div>
        </button>
      </ForgeListItem>
    </div>
  );
};
